import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

interface Faq {
  question: string
  answer: React.ReactNode
}

const LINK = 'text-brand-300 hover:text-brand-200 underline underline-offset-2'

const FAQS: Faq[] = [
  {
    question: 'What is Cai?',
    answer:
      'After running our own 600+ trucking fleet, we realized that the people who build, move, and fix the world get the worst technology tools. At Cai, we’re fixing that. We started by building a digital insurance brokerage, so fleets could get more quotes, faster.',
  },
  {
    question: 'Are you an insurance company?',
    answer:
      "No, Cai is an insurance broker. That means we help you shop for a policy and compare prices across multiple insurance companies. Once you're covered, Cai's platform and brokers work with you and your insurance company to manage your policy: issuing COIs, updating equipment and teams, managing claims, and more.",
  },
  {
    question: 'Is this a group captive?',
    answer:
      "Cai offers traditional insurance policies. We are also opening up spaces in group captive programs subject to eligibility for fleets with 20+ trucks in operation. Let your Cai broker know if you're interested in exploring captive options.",
  },
  {
    question: 'Which insurance providers do you work with?',
    answer:
      'We work with top insurance providers to find the best quotes for your business. Which ones make sense depends on your fleet size and history.',
  },
  {
    question: 'What happens to my documents?',
    answer: (
      <>
        They go to the Cai broker working your file, who reads them and builds your application. Getting you
        quotes means sharing the relevant details with the insurance providers we approach. See our{' '}
        <Link to="/privacy" className={LINK}>
          Privacy Policy
        </Link>{' '}
        for how we handle your information.
      </>
    ),
  },
  {
    question: 'How does Cai use AI?',
    answer: (
      <>
        Cai builds AI tooling for brokers so that they can get more quotes, stay on top of your claims, and
        serve you faster. Your broker is still the informed person you call.{' '}
        <a href="https://blog.cai.ai/how-cai-is-using-ai-to-fix-trucking-insurance" className={LINK}>
          More about how we will and won&apos;t use AI
        </a>
        .
      </>
    ),
  },
  {
    question: 'What does this cost?',
    answer:
      'Nothing, and there is no obligation to buy. You only pay if you decide to purchase a policy we bring you.',
  },
]

function FaqRow({ faq }: { faq: Faq }) {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-ink-600 border-b">
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left">
        <span className="text-ink-50 text-base font-semibold">{faq.question}</span>
        <ChevronDown
          className={cn('text-ink-400 size-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="text-ink-300 max-w-2xl pb-5 text-sm leading-relaxed">{faq.answer}</p>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Page-level context, below the form. Deliberately no eyebrow, the heading carries it. */
export function AboutCaiFaq() {
  return (
    <section className="bg-ink-800">
      <div className="mx-auto w-full max-w-(--maxw) px-4 py-14 sm:px-6 lg:py-20">
        <h2 className="h3 text-ink-50">About Cai</h2>
        <div className="mt-6 max-w-3xl">
          {FAQS.map((faq) => (
            <FaqRow key={faq.question} faq={faq} />
          ))}
        </div>
      </div>
    </section>
  )
}
